<template>
  <div class="gs-overlay" @click.self="close" v-show="!isTeacherPacedChecking">
    <div class="gs-game-card" v-show="!isResuming && !showTeamSelection && !showGameTracking && !isTeacherPacedAutoStarting">
      <!-- HEADER -->
      <div class="gs-header">
        <div class="gs-left">
          <div class="gs-icon-box">
            <i class="fa fa-gamepad"></i>
          </div>
          <div class="gs-header-text">
            <div class="gs-title">START GAME</div>
            <div class="gs-subtitle">CONFIGURE GAME MODE</div>
          </div>
        </div>
        <div class="gs-close" @click="close"><i class="fa fa-times"></i></div>
      </div>

      <!-- BODY CONTENT (SCROLLABLE) -->
      <div class="gs-body">
        <!-- SELECT SLIDES -->
        <div class="gs-section">
          <div class="gs-section-title">
            <span class="gs-bar gs-bar-cyan"></span> SELECT SLIDES
            <span class="gs-badge">{{ selectedSlides.length }} SELECTED</span>
          </div>
          <div class="gs-slides-list">
            <div v-for="slide in slides" :key="slide.id" class="gs-slide-card" :class="{active: isSelected(slide.id)}" @click="toggleSlide(slide.id)">
              <div class="gs-left-part">
                <div class="gs-custom-checkbox" :class="{checked: isSelected(slide.id)}">
                  <i v-if="isSelected(slide.id)" class="fa fa-check"></i>
                </div>
                <div class="gs-thumb" :style="slide.pic ? `background-image: url(${Fn.hashToUrl(slide.pic)})` : ''"></div>
                <div class="gs-content">
                  <div class="gs-name" style="background: transparent">{{ slide.title }}</div>
                  <div class="gs-meta">
                    <span v-if="slide.level" class="gs-gray-text">BLOOM LEVEL {{ slide.level }}</span>
                    <span v-if="slide.type" class="gs-white-bold">{{ slide.type }}</span>
                  </div>
                </div>
              </div>

              <div class="gs-right-part">
                <div v-if="slide.cpa" class="gs-view" @click.stop="previewCpa(slide)"><i class="fa fa-eye"></i> VIEW</div>
                <div v-else-if="!slide.title.includes('VIDEO')" class="gs-view" @click.stop="previewImage(slide.pic)"><i class="fa fa-eye"></i> VIEW</div>
                <div v-if="slide.points" class="gs-points">{{ slide.points }} POINTS</div>
              </div>
            </div>
          </div>
        </div>

        <!-- TIMER -->
        <div class="gs-section">
          <div class="gs-section-title"><span class="gs-bar gs-bar-pink"></span> SET TIMER PER QUESTION</div>

          <div class="gs-timer">
            <div v-for="time in timers" :key="time" class="gs-timer-btn" :class="{active: selectedTime === time}" @click="selectedTime = time">
              {{ Math.floor(time / 60) }} min
            </div>
          </div>
        </div>

        <!-- GIFT CARD SETTINGS -->
        <div class="gs-section" style="padding-top: 10px">
          <div class="gs-section-title"><span class="gs-bar gs-bar-pink"></span> GIFT SETTINGS</div>

          <div class="gs-gift-card-settings" style="display: flex; flex-direction: column; gap: 12px">
            <label style="display: flex; align-items: center; cursor: pointer; color: #1e293b; font-size: 13px">
              <input
                type="radio"
                :value="true"
                v-model="giftCardSettings.allowed"
                style="margin-right: 8px; accent-color: #10b981"
                @change="handleGiftCardAllowedChange" />
              Allow students to use Gifts (Smoke, Shield, Multiplier)
            </label>
            <label style="display: flex; align-items: center; cursor: pointer; color: #1e293b; font-size: 13px">
              <input
                type="radio"
                :value="false"
                v-model="giftCardSettings.allowed"
                style="margin-right: 8px; accent-color: #10b981"
                @change="handleGiftCardAllowedChange" />
              Disable Gifts for students
            </label>

            <div style="display: flex; align-items: center; margin-top: 8px; justify-content: space-between; max-width: 250px">
              <span style="font-size: 13px; color: #1e293b; font-weight: bold">Gift Sound</span>
              <label class="switch" :style="!giftCardSettings.allowed ? 'opacity: 0.5; pointer-events: none;' : ''">
                <input type="checkbox" v-model="giftCardSettings.sound" :disabled="!giftCardSettings.allowed" />
                <span class="slider round"></span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="gs-footer">
        <button class="gs-cancel" @click="close">CANCEL</button>
        <button class="gs-start" @click="handleNext" :disabled="selectedSlides.length === 0" :class="{disabled: selectedSlides.length === 0}">
          NEXT <i class="fa fa-play" style="font-size: 10px; margin-left: 4px"></i>
        </button>
      </div>
    </div>
    <!-- IMAGE PREVIEW OVERLAY -->
    <div v-if="previewPicUrl" class="gs-preview-overlay" @click.stop="closePreview">
      <div class="gs-preview-close" @click.stop="closePreview"><i class="fa fa-times"></i></div>
      <img :src="Fn.hashToUrl(previewPicUrl)" alt="Preview" class="gs-preview-image" @click.stop />
    </div>

    <!-- CPA PREVIEW OVERLAY -->
    <div v-if="previewCpaData" class="gs-preview-overlay" @click.stop="closePreview">
      <div class="gs-cpa-modal" @click.stop>
        <div class="gs-cpa-close" @click.stop="closePreview"><i class="fa fa-times"></i></div>
        <div class="gs-cpa-question" v-html="previewCpaData.abstractData"></div>
        <div class="gs-cpa-options">
          <div v-for="(opt, idx) in previewCpaData.options" :key="idx" class="gs-cpa-option">
            <i v-if="opt.on" class="fa fa-check gs-cpa-tick"></i>
            <span :class="{'gs-cpa-indent': !opt.on}">{{ opt.val }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- TEAM SELECTION MODAL -->
    <TeamSelectionModal v-if="showTeamSelection" :gameConfig="gamePayload" @close="handleTeamPreviewClose" @start="handleStartFromTeam" />

    <!-- LOADING OVERLAY FOR TEACHER PACED AUTO START -->
    <div
      class="gs-game-card"
      style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 300px;
        background: transparent;
        border: none;
        box-shadow: none;
      "
      v-if="isTeacherPacedAutoStarting && !showTeamSelection && !showGameTracking">
      <div style="font-size: 40px; color: #00ffff; margin-bottom: 20px"><i class="fa fa-spinner fa-spin"></i></div>
      <div style="font-size: 14px; color: #fff; font-weight: bold; letter-spacing: 2px">PREPARING GAME...</div>
    </div>

    <!-- GAME TRACKING PAGE -->
    <GameTrackingPage v-if="showGameTracking" :gameConfig="gamePayload" :teams="activeTeams" :isDashboard="isDashboardMode" @end-game="handleEndGame" />
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import TeamSelectionModal from './TeamSelectionModal.vue'
import GameTrackingPage from './GameTrackingPage.vue'

export default {
  name: 'GameSetting',
  components: {TeamSelectionModal, GameTrackingPage},
  props: {
    resumeGameId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isResuming: !!this.resumeGameId,
      isTeacherPacedChecking: false,
      isTeacherPacedAutoStarting: false,
      selectedSlideIds: [],
      selectedSlides: [],
      selectedTime: 300,
      giftCardSettings: {allowed: true, sound: true},
      timers: [300, 600, 900],
      previewPicUrl: null,
      previewCpaData: null,
      showTeamSelection: false,
      showGameTracking: false,
      activeTeams: [],
      gamePayload: null,
      BloomLabels: ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'],
    }
  },
  computed: {
    ...mapGetters(['getPageById', 'sessionId', 'getPageId']),
    ...mapGetters('teacher', ['questions']),

    slides() {
      if (!this.questions || !this.session?.task?.pages) return []

      const map = new Map()

      // Step 1: Group questions by normalized pageId
      this.questions.forEach((q) => {
        const rawId = q.id && q.id.includes('::') ? q.id : q.page || q.id || ''
        const pageId = this.getNormalizedPageId(rawId)

        const pageData = this.getPageById(pageId)
        if (!pageData || !pageData._id) return

        if (!map.has(pageId)) {
          map.set(pageId, {
            id: pageId,
            pic: pageData.pic || '',
            questions: [],
          })
        }

        map.get(pageId).questions.push(q)
      })
      return this.session.task.pages
        .map((page) => {
          const pageId = this.getNormalizedPageId(page._id)

          if (!map.has(pageId)) return null

          const slide = map.get(pageId)
          const q = slide.questions[0]

          let titleType = q.type ? q.type.toUpperCase() : 'TEXT'
          if (q.type === 'choice') titleType = 'MULTIPLE CHOICE'
          if (q.id && q.id.includes('::')) titleType = 'VIDEO'

          return {
            id: slide.id,
            pic: slide.pic,

            title: `${titleType} QUESTION`,
            level: q.bloom?.[0] ?? null,
            type: q.bloom?.[0] != null && this.BloomLabels[q.bloom[0]] ? this.BloomLabels[q.bloom[0]].toUpperCase() : null,

            points: q.type === 'choice' && (q.options || []).some((o) => o.on === true) ? 15 : null,

            cpa: q.cpa?.outcome || null,
            abstractData: q.abstract?.data || '',
            options: q.options || [],

            questions: slide.questions,
          }
        })
        .filter(Boolean)
    },
    isTeacherPaced() {
      const status = this.$store.state.session && this.$store.state.session.status
      return status && status !== 'student' && status !== 'close'
    },
    isDashboardMode() {
      const path = (this.$route && this.$route.path) || window.location.pathname
      return path.includes('/d/')
    },
    session() {
      return this.$store.state.session
    },
  },

  watch: {
    'session.gameOnTracking': {
      handler(val, oldVal) {
        // Sync state if it changed remotely (e.g. status 1 -> 2)
        // We no longer close the modal automatically here (!val check),
        // because GameTrackingPage already handles the 'end-game' flow
        // by listening to the game-on service socket. Removing this
        // prevents premature closure during dashboard refresh.
        if (val && this.showGameTracking) {
          if (val.gameConfig) this.gamePayload = val.gameConfig
          if (val.teams) this.activeTeams = val.teams
        }
      },
    },
  },

  mounted() {
    document.body.appendChild(this.$el)
    if (this.resumeGameId) {
      this.handleResumeGame(this.resumeGameId)
    } else {
      const status = this.$store.state.session && this.$store.state.session.status
      if (status === 'live') {
        this.isTeacherPacedChecking = true
        const pageId = this.getPageId
        const currentQ =
          this.questions &&
          this.questions.find((q) => {
            const qPageId = q.id && q.id.includes('::') ? q.id : q.page
            return this.getNormalizedPageId(qPageId) === pageId
          })

        if (pageId && currentQ) {
          this.isTeacherPacedChecking = false
          this.isTeacherPacedAutoStarting = true
          this.selectedSlides = [pageId]
          this.selectedTime = 300
          this.handleNext()
        } else {
          this.isTeacherPacedChecking = false
          if (this.$message) {
            this.$message.warning('There is no question in this slide')
          } else {
            alert('There is no question in this slide')
          }
          this.close()
        }
      }
    }
  },
  destroyed() {
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el)
    }
  },
  methods: {
    getNormalizedPageId(page) {
      if (!page) return null
      return page.includes('::') ? page.split('::')[1] : page
    },

    toggleSlide(id) {
      if (this.selectedSlides.includes(id)) {
        this.selectedSlides = this.selectedSlides.filter((x) => x !== id)
      } else {
        this.selectedSlides.push(id)
      }
    },
    handleGiftCardAllowedChange() {
      if (!this.giftCardSettings.allowed) {
        this.giftCardSettings.sound = false
      } else {
        this.giftCardSettings.sound = true
      }
    },

    isSelected(id) {
      return this.selectedSlides.includes(id)
    },
    close() {
      this.$emit('close')
    },
    previewImage(picUrl) {
      if (picUrl) {
        this.previewPicUrl = picUrl
      }
    },
    previewCpa(slide) {
      if (slide && slide.cpa) {
        this.previewCpaData = slide
      }
    },
    closePreview() {
      this.previewPicUrl = null
      this.previewCpaData = null
    },
    handleTeamPreviewClose() {
      this.showTeamSelection = false
      if (this.isTeacherPacedAutoStarting) {
        this.$emit('close')
      }
    },
    async handleNext() {
      const fullQuestions = this.selectedSlides.flatMap((pageId) => {
        const slide = this.slides.find((s) => s.id === pageId)
        if (!slide) return []

        return slide.questions.map((q) => ({
          _id: q._id,
          id: q._id || q.id,
          page: pageId,
          type: q.type,
          multi: q.multi,
          options: q.options || [],
          score: q.score || {},
          data: q.data || null,
          pic: slide.pic,
          title: slide.title,
          points: slide.points || (q.score?.points ?? 15),
        }))
      })
      const orderedSlides = this.slides.filter((slide) => this.selectedSlides.includes(slide.id))
      const slidesPayload = orderedSlides.map((slide, slideIndex) => ({
        pageId: slide.id,
        serial: slideIndex + 1,

        pic: slide.pic,
        title: slide.title,
        points: slide.points,

        questions: slide.questions.map((q, qIndex) => ({
          _id: q._id,
          id: q._id || q.id,

          slideSerial: slideIndex + 1,
          questionSerial: qIndex + 1,

          type: q.type,
          multi: q.multi,
          options: q.options || [],
          score: q.score || {},
          data: q.data || null,
        })),
      }))

      const payload = {
        slides: slidesPayload,
        slideIds: this.selectedSlides,
        timing: this.selectedTime,
        time: this.selectedTime,
        giftCardSettings: this.giftCardSettings,
      }

      try {
        if (window.App) {
          const record = await App.service('game-on').create({
            teamGameId: this.session.gameOn?.teamGameId,
            sessionId: this.sessionId,
            pageIds: [...new Set(fullQuestions.map((q) => q.page))],
            timePerQuestion: this.selectedTime,
            giftCardSettings: this.giftCardSettings,
          })

          payload.gameOnRecord = record
        }
      } catch (err) {
        console.error('Failed to create game-on record:', err)
      }
      this.gamePayload = payload
      this.showTeamSelection = true
    },

    async handleStartFromTeam(finalPayload) {
      if (finalPayload.gameOnRecord) {
        this.$set(this.gamePayload, 'gameOnRecord', finalPayload.gameOnRecord)
      }
      this.showTeamSelection = false
      this.showGameTracking = true
      this.activeTeams = finalPayload.teams

      // Sync to session for projector view
      try {
        await this.$store.dispatch('upSession', {
          gameOnTracking: {
            gameConfig: this.gamePayload,
            teams: finalPayload.teams,
          },
        })
      } catch (e) {
        console.error('Failed to sync gameOnTracking to session:', e)
      }
    },
    async handleEndGame(updatedConfig) {
      if (updatedConfig) {
        this.gamePayload = updatedConfig
      }

      // Clear from session
      try {
        await this.$store.dispatch('upSession', {gameOnTracking: null})
      } catch (e) {
        console.error('Failed to clear gameOnTracking from session:', e)
      }

      this.showGameTracking = false
      this.$emit('end-game', this.gamePayload)
      this.$emit('close')
    },
    async handleResumeGame(recordId) {
      try {
        if (!window.App) {
          this.isResuming = false
          return
        }

        const record = await App.service('game-on').get(recordId)

        if (!record || (record.status !== 1 && record.status !== 2) || record.leftGameMode) {
          this.isResuming = false
          return
        }

        if (!this.slides || !this.slides.length) {
          console.error('Slides not loaded. Cannot resume game.')
          this.isResuming = false
          return
        }

        const orderedSlides = (record.pageIds || []).map((pageId) => this.slides.find((s) => s.id === pageId)).filter(Boolean)

        const slidesPayload = orderedSlides.map((slide, slideIndex) => ({
          pageId: slide.id,
          serial: slideIndex + 1,

          pic: slide.pic,
          title: slide.title,
          points: slide.points,

          questions: (slide.questions || []).map((q, qIndex) => ({
            _id: q._id,
            id: q._id || q.id,

            slideSerial: slideIndex + 1,
            questionSerial: qIndex + 1,

            type: q.type,
            multi: q.multi,
            options: q.options || [],
            score: q.score || {},
            data: q.data || null,
          })),
        }))

        this.gamePayload = {
          slides: slidesPayload,
          slideIds: record.pageIds,
          timing: record.timePerQuestion || 300,
          time: record.timePerQuestion || 300,
          gameOnRecord: record,
          giftCardSettings: record.giftCardSettings || {allowed: true, sound: true},
        }

        this.giftCardSettings = record.giftCardSettings || {allowed: true, sound: true}

        const dbTeams = record.teams || []
        const dbMembers = record.members || []

        const classMembers = this.$store?.state?.teacher?.rooms?.members || []

        const coreColors = [
          '#FF3366',
          '#FF9933',
          '#FFCC00',
          '#33CC33',
          '#3399FF',
          '#9933FF',
          '#FF66B2',
          '#FFB266',
          '#FFE566',
          '#66E566',
          '#66B2FF',
          '#B266FF',
          '#FF0000',
          '#FF8000',
          '#FFFF00',
          '#00FF00',
          '#0000FF',
          '#8000FF',
          '#CC0000',
          '#CC6600',
          '#CCCC00',
          '#00CC00',
          '#0000CC',
          '#6600CC',
        ]

        let cIdx = 0

        this.activeTeams = dbTeams.map((t, idx) => {
          const teamColor = coreColors[idx % coreColors.length]

          const teamMembers = dbMembers
            .filter((m) => m.teamId === t.id)
            .map((m) => {
              const user = classMembers.find((c) => c._id === m.uid) || {}

              const mName = typeof m.name === 'string' ? m.name : Array.isArray(m.name) ? m.name.join(' ') : null

              const cName = user.nickname || user.name

              return {
                id: m.uid,
                name: mName || cName || 'Unknown User',
                avatar: m.avatar || user.avatar || user.pic || '',
                score: m.score || 0,
                originalMemberData: m,
                colorIndex: cIdx++,
              }
            })

          return {
            id: t.id,
            name: t.name || `TEAM ${t.id}`,
            score: t.score || 0,
            progress: t.progress || 0,
            color: teamColor,
            members: teamMembers,
          }
        })

        this.showTeamSelection = false
        this.showGameTracking = true
        this.isResuming = false

        try {
          await this.$store.dispatch('upSession', {
            gameOnTracking: {
              gameConfig: this.gamePayload,
              teams: this.activeTeams,
            },
          })
        } catch (e) {
          console.error('Failed to sync gameOnTracking during resume:', e)
        }
      } catch (err) {
        console.error('Failed to resume game-on tracking from record:', err)
        this.isResuming = false
      }
    },
  },
}
</script>

<
<style scoped>
/* Switch Toggle */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e1;
  transition: 0.4s;
}
.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
}
input:checked + .slider {
  background-color: #10b981;
}
input:focus + .slider {
  box-shadow: 0 0 1px #10b981;
}
input:checked + .slider:before {
  transform: translateX(20px);
}
.slider.round {
  border-radius: 24px;
}
.slider.round:before {
  border-radius: 50%;
}

.gs-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
}

.gs-game-card {
  width: 640px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  color: #1e293b;
  font-family: Arial, sans-serif;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.gs-body {
  flex: 1;
  overflow-y: auto;
}

/* Custom Scrollbar for gs-body */
.gs-body::-webkit-scrollbar {
  width: 6px;
}

.gs-body::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.gs-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.gs-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* HEADER */
.gs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}

.gs-left {
  display: flex;
  align-items: center;
}

.gs-icon-box {
  background: transparent;
  color: #10b981;
  font-size: 24px;
  margin-right: 12px;
}

.gs-header-text {
  display: flex;
  flex-direction: column;
}

.gs-title {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 1px;
}

.gs-subtitle {
  font-size: 10px;
  color: #10b981;
  letter-spacing: 0.5px;
}

.gs-close {
  cursor: pointer;
  font-size: 18px;
  color: #64748b;
  transition: color 0.2s;
}

.gs-close:hover {
  color: #1e293b;
}

/* SECTION */
.gs-section {
  padding: 24px 24px 10px 24px;
}

.gs-section-title {
  display: flex;
  align-items: center;
  font-size: 13px;
  font-weight: bold;
  color: #1e293b;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
}

.gs-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  border-radius: 2px;
  margin-right: 8px;
}

.gs-bar-cyan {
  background-color: #10b981;
}

.gs-bar-pink {
  background-color: #10b981;
}

.gs-badge {
  margin-left: auto;
  border: 1px solid #e2e8f0;
  color: #64748b;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  background-color: #f8fafc;
}

/* SLIDES LIST */
.gs-slides-list {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 6px;
  margin-right: -6px;
}

/* Custom Scrollbar */
.gs-slides-list::-webkit-scrollbar {
  width: 6px;
}

.gs-slides-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.gs-slides-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.gs-slides-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* SLIDE CARD */
.gs-slide-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s;
}

.gs-slide-card:hover {
  background: #f8fafc;
}

.gs-slide-card.active {
  border: 1px solid #10b981;
  background: #ecfdf5;
}

.gs-left-part {
  display: flex;
  align-items: center;
  flex: 1;
}

.gs-custom-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #ffffff;
  background: #ffffff;
  transition: all 0.2s;
  flex-shrink: 0;
}

.gs-custom-checkbox.checked {
  background-color: #10b981;
  border-color: #10b981;
}

.gs-custom-checkbox i {
  font-size: 12px;
}

.gs-thumb {
  width: 64px;
  height: 44px;
  background-color: #e2e8f0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  margin-left: 16px;
  border-radius: 4px;
  flex-shrink: 0;
}

.gs-content {
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.gs-name {
  font-size: 13px;
  font-weight: bold;
  color: #1e293b;
  margin-bottom: 4px;
}

.gs-meta {
  display: flex;
  align-items: center;
}

.gs-gray-text {
  color: #64748b;
  font-size: 9px;
  font-weight: bold;
  letter-spacing: 0.5px;
}

.gs-white-bold {
  color: #334155;
  font-weight: bold;
  font-size: 10px;
  margin-left: 6px;
  letter-spacing: 0.5px;
}

.gs-right-part {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: 38px;
  min-width: 70px;
}

.gs-view {
  font-size: 10px;
  color: #10b981;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: bold;
}

.gs-view:hover {
  text-decoration: underline;
}

.gs-points {
  font-size: 10px;
  color: #f59e0b;
  font-weight: bold;
}

/* TIMER */
.gs-timer {
  display: flex;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
}

.gs-timer-btn {
  flex: 1;
  text-align: center;
  padding: 14px;
  cursor: pointer;
  color: #64748b;
  font-size: 13px;
  font-weight: bold;
  background: #ffffff;
  transition: all 0.2s;
}

.gs-timer-btn:not(:last-child) {
  border-right: 1px solid #e2e8f0;
}

.gs-timer-btn:hover:not(.active) {
  background: #f8fafc;
}

.gs-timer-btn.active {
  background-color: #10b981;
  color: #ffffff;
  box-shadow: none;
  z-index: 1;
}

.gs-warning {
  font-size: 9px;
  text-align: center;
  color: #f43f5e;
  font-weight: bold;
  letter-spacing: 0.5px;
}

/* FOOTER */
.gs-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 15px 24px;
  gap: 16px;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
}

.gs-cancel {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  padding: 10px;
  transition: color 0.2s;
  letter-spacing: 0.5px;
}

.gs-cancel:hover {
  color: #1e293b;
}

.gs-start {
  background: #10b981;
  color: #ffffff;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  font-size: 12px;
  display: flex;
  align-items: center;
  transition: background 0.2s, transform 0.1s;
  letter-spacing: 0.5px;
}

.gs-start:hover {
  background: #059669;
  transform: scale(1.02);
}

/* PREVIEW MODAL */
.gs-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100000;
}

.gs-preview-image {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid #e2e8f0;
}

.gs-preview-close {
  position: absolute;
  top: 24px;
  right: 24px;
  color: #ffffff;
  font-size: 32px;
  cursor: pointer;
  transition: color 0.2s;
  z-index: 100001;
}

.gs-preview-close:hover {
  color: #e2e8f0;
}

.gs-cpa-close {
  position: absolute;
  top: 16px;
  right: 20px;
  color: #94a3b8;
  font-size: 20px;
  cursor: pointer;
  transition: color 0.2s;
  z-index: 10;
}

.gs-cpa-close:hover {
  color: #1e293b;
}

.gs-cpa-modal {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 32px;
  font-size: 16px;
  color: #1e293b;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  font-family: Arial, sans-serif;
  position: relative;
}

.gs-cpa-question {
  margin-bottom: 12px;
  line-height: 1.4;
  padding-right: 24px;
}

.gs-cpa-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gs-cpa-option {
  display: flex;
  align-items: flex-start;
  line-height: 1.4;
}

.gs-cpa-tick {
  color: #10b981; /* green check */
  margin-right: 8px;
  margin-top: 3px;
  font-size: 12px;
  width: 14px;
}

.gs-cpa-indent {
  margin-left: 22px;
}

.gs-start:disabled,
.gs-start.disabled {
  background-color: #cbd5e1; /* light grey */
  color: #64748b; /* muted text */
  cursor: not-allowed; /* blocked cursor */
  opacity: 0.7; /* slight fade */
  pointer-events: none; /* prevent any interaction */
  box-shadow: none; /* remove shadow if any */
}

/* ── Mobile optimised view ───────────────────────────────────────────── */
@media (max-width: 600px) {
  /* Overlay — no padding, items start at top */
  .gs-overlay {
    padding: 0;
    align-items: flex-start;
  }

  /* Card fills the full screen */
  .gs-game-card {
    width: 100%;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    border: none;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  /* Header — tighter padding */
  .gs-header {
    padding: 12px 16px;
    flex-shrink: 0;
  }

  .gs-icon-box {
    font-size: 20px;
    margin-right: 10px;
  }

  .gs-title {
    font-size: 14px;
  }

  .gs-subtitle {
    font-size: 9px;
  }

  /* Section — leaner spacing */
  .gs-section {
    padding: 16px 14px 8px 14px;
  }

  /* Slides list — grow to fill remaining space */
  .gs-slides-list {
    max-height: none;
    flex: 1;
    overflow-y: auto;
  }

  /* Slide card — keep horizontal layout, tighten gaps */
  .gs-slide-card {
    padding: 10px 12px;
    margin-bottom: 8px;
  }

  /* Hide thumbnail on mobile to save space */
  .gs-thumb {
    display: none;
  }

  .gs-content {
    margin-left: 10px;
  }

  .gs-name {
    font-size: 12px;
  }

  .gs-gray-text {
    font-size: 8px;
  }

  .gs-white-bold {
    font-size: 9px;
  }

  /* Right-part — shrink min-width */
  .gs-right-part {
    min-width: 56px;
    height: auto;
    gap: 6px;
  }

  .gs-view {
    font-size: 9px;
  }

  .gs-points {
    font-size: 9px;
  }

  /* Timer buttons — full width, comfortable tap targets */
  .gs-timer-btn {
    padding: 12px 10px;
    font-size: 12px;
  }

  .gs-warning {
    font-size: 8px;
  }

  /* Footer — full-width buttons stacked horizontally */
  .gs-footer {
    padding: 12px 14px;
    flex-shrink: 0;
  }

  .gs-cancel {
    font-size: 11px;
  }

  .gs-start {
    padding: 10px 20px;
    font-size: 11px;
  }

  /* CPA preview modal — fits small screens */
  .gs-cpa-modal {
    width: 95%;
    padding: 20px 16px;
    font-size: 14px;
    max-height: 85vh;
  }

  .gs-cpa-question {
    padding-right: 28px;
  }

  /* Image preview — already responsive via max-width/max-height */
}
</style>
